// Skærmlaget: én skærm ad gangen oven på kortet. Kortet ses altid bagved.

import { h } from '../dom';

/**
 * UTJEKKET-mærket er til os under udvikling og kun med `?debug`; spillere ser kilden alene
 * (DESIGN.md: Netatlas). Playtest: mærket så ud som en fejl i spillet.
 */
export const SHOW_UNCHECKED = !import.meta.env.PROD && typeof location !== 'undefined' && new URLSearchParams(location.search).has('debug');

export type ScreenId = 'title' | 'home' | 'levelstart' | 'yearend' | 'result' | 'settings' | 'atlas' | 'room' | 'pause';

/**
 * Skærme, der dukker op midt i spillet, mens spilleren har hånden på tastaturet. IGN-løkke 2:
 * mellemrum (pause) ramte gaven eller "Prøv igen", fordi knappen fik fokus. Her får skærmen selv
 * fokus; Tab går til knapperne, og mellemrum gør intet, før spilleren har valgt en knap.
 */
const INTERRUPTS: ReadonlySet<ScreenId> = new Set(['yearend', 'result']);

export class Screens {
  readonly el = h('section', { class: 'screens', hidden: true, tabindex: '-1' });
  current: ScreenId | null = null;
  private prevFocus: Element | null = null;
  /** Skærmen skifter (eller kommer frem): beskeder fra før gælder ikke længere. */
  onChange: ((id: ScreenId | null) => void) | null = null;

  constructor() {
    this.el.addEventListener('keydown', (e) => {
      // Mellemrum på selve laget ruller ikke kortet væk under spilleren.
      if (e.target === this.el && (e.key === ' ' || e.key === 'Enter')) e.preventDefault();
    });
  }

  show(id: ScreenId, content: HTMLElement): void {
    if (!this.current) this.prevFocus = document.activeElement;
    if (id !== this.current) this.onChange?.(id);
    this.current = id;
    this.el.dataset.screen = id;
    this.el.replaceChildren(content);
    this.el.hidden = false;
    // Laget genbruges: uden dette åbnede tabsskærmen rullet ned, hvor årsskiftets gavevalg slap,
    // og overskriften ("MØRKT I …") stod skjult over kanten.
    this.el.scrollTop = 0;
    this.el.classList.remove('in');
    void this.el.offsetWidth;
    this.el.classList.add('in');
    // Det fremhævede valg får fokus, ikke bare den første knap (som tit er Tilbage).
    if (INTERRUPTS.has(id)) {
      this.el.focus({ preventScroll: true });
      return;
    }
    const first = content.querySelector<HTMLElement>('[autofocus]') ?? content.querySelector<HTMLElement>('.btn.primary, button:not([disabled])');
    first?.focus({ preventScroll: true });
  }

  hide(): void {
    if (!this.current) return;
    this.onChange?.(null);
    this.current = null;
    this.el.hidden = true;
    this.el.replaceChildren();
    if (this.prevFocus instanceof HTMLElement) this.prevFocus.focus({ preventScroll: true });
  }
}

/** Stjerner som ikoner med tekst ved siden af (farve er ikke eneste signal). */
export function starsRow(n: number, icons: { on: string; off: string }, big = false): HTMLElement {
  const row = h('div', { class: `stars${big ? ' big' : ''}`, role: 'img', 'aria-label': `${n} af 3 stjerner` });
  for (let i = 0; i < 3; i++) row.append(h('span', { class: `star ${i < n ? 'on' : 'off'}`, style: `--i:${i}`, html: i < n ? icons.on : icons.off }));
  return row;
}
